import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

const FILTERS = [
  ["unanswered", "Unanswered"],
  ["all", "All"],
  ["answered", "Answered"],
];

function TriageItem({ v, onTogglePick }) {
  return (
    <Link to={`/video/${v.video_id}`} data-testid={`coach-item-${v.video_id}`}
          className="flex items-center gap-5 p-4 border border-white/10 bg-white/[0.02] hover:border-[#C9A227]/40 transition group">
      <div className="relative w-28 h-20 bg-black border border-white/10 overflow-hidden flex-shrink-0">
        <video src={`${API}/videos/${v.video_id}/file`} muted playsInline preload="metadata"
               className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-display text-2xl truncate group-hover:text-[#C9A227] transition">{v.title}</h3>
          {!v.has_coach_reply && (
            <span data-testid={`coach-needs-reply-${v.video_id}`}
                  className="overline px-2 py-1 border border-[#C9A227] text-[#C9A227]">
              Needs reply
            </span>
          )}
        </div>
        <div className="text-white/50 text-xs mt-1 flex items-center gap-4">
          <span>by {v.author_name}</span>
          <span>{v.comment_count} comment{v.comment_count === 1 ? "" : "s"}</span>
          <span>{new Date(v.created_at).toLocaleDateString()}</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={(e) => onTogglePick(e, v)} data-testid={`coach-pick-star-${v.video_id}`}
                title={v.is_coach_pick ? "Remove Coach Pick of the Week" : "Set as Coach Pick of the Week"}
                className={`text-2xl leading-none transition ${v.is_coach_pick ? "text-[#C9A227]" : "text-white/20 hover:text-[#C9A227]"}`}>
          {v.is_coach_pick ? "★" : "☆"}
        </button>
        <div className="text-white/40 group-hover:text-[#C9A227] transition text-2xl">→</div>
      </div>
    </Link>
  );
}

export default function CoachDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("unanswered"); // unanswered | all | answered

  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (user && user.role !== "admin") { nav("/feed"); return; }
    if (!user) return;
    api.get("/coach/videos")
      .then((r) => { setVideos(r.data); setLoading(false); })
      .catch((e) => { setErr(formatApiError(e)); setLoading(false); });
  }, [user, nav]);

  if (!user || user.role !== "admin") return null;

  const filtered = videos.filter((v) => {
    if (filter === "unanswered") return !v.has_coach_reply;
    if (filter === "answered") return v.has_coach_reply;
    return true;
  });

  const unansweredCount = videos.filter((v) => !v.has_coach_reply).length;

  const togglePick = async (e, v) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const { data } = await api.post(`/videos/${v.video_id}/coach-pick`, { pick: !v.is_coach_pick });
      setVideos((list) => list.map((x) => ({
        ...x,
        is_coach_pick: x.video_id === v.video_id ? data.is_coach_pick : false,
      })));
    } catch (err) {
      console.error("Coach pick toggle failed:", err);
    }
  };

  return (
    <div data-testid="coach-dashboard" className="min-h-screen pt-32 pb-24 px-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="overline">Coach console</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3">Triage Bay</h1>
            <p className="text-white/60 mt-2">
              {unansweredCount > 0
                ? <><span className="text-[#C9A227] font-semibold">{unansweredCount}</span> swing{unansweredCount === 1 ? "" : "s"} waiting on your eye.</>
                : "All caught up. Nice work, coach."}
            </p>
          </div>
          <div className="flex gap-2" data-testid="coach-filters">
            {FILTERS.map(([key, label]) => (
              <button key={key} data-testid={`coach-filter-${key}`} onClick={() => setFilter(key)}
                      className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${
                        filter === key
                          ? "bg-[#C9A227] text-[#050A08] border-[#C9A227]"
                          : "border-white/15 text-white/70 hover:border-[#C9A227] hover:text-[#C9A227]"
                      }`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {err && <div className="text-red-400 mb-6" data-testid="coach-error">{err}</div>}

        {loading ? (
          <div className="text-white/50">Loading triage queue...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 border border-white/10 bg-white/[0.02] text-center" data-testid="coach-empty">
            <h3 className="font-display text-3xl">Inbox zero.</h3>
            <p className="text-white/60 mt-3">No swings match this filter.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="coach-list">
            {filtered.map((v) => (
              <TriageItem key={v.video_id} v={v} onTogglePick={togglePick} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
